import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useMst } from '../../model/Root';
import { EventLog, EventListParams } from '../../types';
import { fetchEvents } from '../../services/eventsApi';
import { getOutcomeColor } from '../../utils/events';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

const DEFAULT_LIMIT = 50;
const LIMIT_OPTIONS = [25, 50, 100, 200];
const OUTCOME_OPTIONS = ['denied', 'failed', 'ok'];
const CATEGORY_OPTIONS = ['auth', 'entitlement', 'media_token'];

const formatTimestamp = (event: EventLog, fallbackLabel: string) => {
  const timestamp = event.occurredAt || event.timestamp || event.createdAt;
  if (!timestamp) return fallbackLabel;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }
  return parsed.toLocaleString('fi-FI', {
    timeZone: 'Europe/Helsinki',
    dateStyle: 'short',
    timeStyle: 'medium',
  });
};

const EventsScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    errorStore: { setError },
  } = useMst();

  const [formFilters, setFormFilters] = useState<EventListParams>({ limit: DEFAULT_LIMIT, offset: 0 });
  const [appliedFilters, setAppliedFilters] = useState<EventListParams>({ limit: DEFAULT_LIMIT, offset: 0 });
  const [events, setEvents] = useState<EventLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchEvents(appliedFilters);
      const limit = response.pagination?.limit ?? appliedFilters.limit ?? DEFAULT_LIMIT;
      const offset = response.pagination?.offset ?? appliedFilters.offset ?? 0;
      const total = response.pagination?.total ?? response.data?.length ?? 0;
      setEvents(response.data ?? []);
      setPagination({ total, limit, offset });
    } catch (err) {
      console.error(err);
      setError('general_error_message');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, setError]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleTextChange =
    (key: keyof EventListParams) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormFilters((prev) => ({ ...prev, [key]: value || undefined }));
    };

  const handleOutcomeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFormFilters((prev) => ({ ...prev, outcome: value || undefined }));
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFormFilters((prev) => ({ ...prev, category: value || undefined }));
  };

  const handleApply = () => {
    const limit = formFilters.limit ?? DEFAULT_LIMIT;
    setAppliedFilters({ ...formFilters, limit, offset: 0 });
  };

  const handleReset = () => {
    const base = { limit: DEFAULT_LIMIT, offset: 0 };
    setFormFilters(base);
    setAppliedFilters(base);
  };

  const changePage = (page: number) => {
    const limit = pagination.limit || DEFAULT_LIMIT;
    setAppliedFilters((prev) => ({ ...prev, offset: (page - 1) * limit, limit }));
  };

  const handleLimitChange = (event: SelectChangeEvent<string>) => {
    const parsed = Number(event.target.value);
    const nextLimit = Number.isNaN(parsed) || parsed <= 0 ? DEFAULT_LIMIT : parsed;
    setFormFilters((prev) => ({ ...prev, limit: nextLimit }));
    setAppliedFilters((prev) => ({ ...prev, limit: nextLimit, offset: 0 }));
  };

  const totalCount = pagination.total || events.length || 0;
  const currentPage = Math.floor((pagination.offset || 0) / (pagination.limit || DEFAULT_LIMIT)) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / (pagination.limit || DEFAULT_LIMIT)));
  const showingFrom = events.length === 0 ? 0 : (pagination.offset || 0) + 1;
  const showingTo = events.length === 0 ? 0 : (pagination.offset || 0) + events.length;

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    if (appliedFilters.search) {
      chips.push({ key: 'search', label: `${t('search')}: ${appliedFilters.search}` });
    }
    if (appliedFilters.requestId) {
      chips.push({ key: 'requestId', label: `requestId=${appliedFilters.requestId}` });
    }
    if (appliedFilters.traceId) {
      chips.push({ key: 'traceId', label: `traceId=${appliedFilters.traceId}` });
    }
    if (appliedFilters.userId) {
      chips.push({ key: 'userId', label: `userId=${appliedFilters.userId}` });
    }
    if (appliedFilters.event) {
      chips.push({ key: 'event', label: appliedFilters.event });
    }
    if (appliedFilters.category) {
      chips.push({ key: 'category', label: appliedFilters.category });
    }
    if (appliedFilters.outcome) {
      chips.push({ key: 'outcome', label: appliedFilters.outcome });
    }
    if (appliedFilters.reasonCode) {
      chips.push({ key: 'reasonCode', label: appliedFilters.reasonCode });
    }
    if (appliedFilters.route) {
      chips.push({ key: 'route', label: appliedFilters.route });
    }
    if (appliedFilters.method) {
      chips.push({ key: 'method', label: appliedFilters.method });
    }
    if (appliedFilters.from) {
      chips.push({ key: 'from', label: `from=${appliedFilters.from}` });
    }
    if (appliedFilters.to) {
      chips.push({ key: 'to', label: `to=${appliedFilters.to}` });
    }
    return chips;
  }, [appliedFilters, t]);

  return (
    <Layout title={t('events.title')}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.search')}
              placeholder={t('events.filters.search_placeholder')}
              value={formFilters.search ?? ''}
              onChange={handleTextChange('search')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.request_id')}
              value={formFilters.requestId ?? ''}
              onChange={handleTextChange('requestId')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.trace_id')}
              value={formFilters.traceId ?? ''}
              onChange={handleTextChange('traceId')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('events.filters.user_id')}
              value={formFilters.userId ?? ''}
              onChange={handleTextChange('userId')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.event')}
              value={formFilters.event ?? ''}
              onChange={handleTextChange('event')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('events.filters.category')}</InputLabel>
              <Select
                value={formFilters.category ?? ''}
                label={t('events.filters.category')}
                onChange={handleCategoryChange}
              >
                <MenuItem value="">{t('common.show_all')}</MenuItem>
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('events.filters.outcome')}</InputLabel>
              <Select
                value={formFilters.outcome ?? ''}
                label={t('events.filters.outcome')}
                onChange={handleOutcomeChange}
              >
                <MenuItem value="">{t('common.show_all')}</MenuItem>
                {OUTCOME_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`events.outcome_${option}`, { defaultValue: option })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.reason_code')}
              value={formFilters.reasonCode ?? ''}
              onChange={handleTextChange('reasonCode')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.route')}
              value={formFilters.route ?? ''}
              onChange={handleTextChange('route')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.method')}
              value={formFilters.method ?? ''}
              onChange={handleTextChange('method')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.from')}
              placeholder="2026-01-01T00:00:00Z"
              value={formFilters.from ?? ''}
              onChange={handleTextChange('from')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('events.filters.to')}
              placeholder="2026-01-31T23:59:59Z"
              value={formFilters.to ?? ''}
              onChange={handleTextChange('to')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('events.filters.limit')}</InputLabel>
              <Select
                value={String(formFilters.limit ?? DEFAULT_LIMIT)}
                label={t('events.filters.limit')}
                onChange={handleLimitChange}
              >
                {LIMIT_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={handleReset}>{t('events.filters.reset')}</Button>
              <Button variant="contained" onClick={handleApply}>
                {t('events.filters.apply')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {activeFilterChips.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          {activeFilterChips.map((chip) => (
            <Chip key={chip.key} label={chip.label} size="small" />
          ))}
        </Stack>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">{t('events.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('events.pagination_summary', {
              from: showingFrom,
              to: showingTo,
              total: totalCount,
            })}
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} /> <Typography>{t('events.loading')}</Typography>
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('events.no_results')}
          </Typography>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <MuiTable size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('events.table.timestamp')}</TableCell>
                  <TableCell>{t('events.table.event')}</TableCell>
                  <TableCell>{t('events.table.category')}</TableCell>
                  <TableCell>{t('events.table.outcome')}</TableCell>
                  <TableCell>{t('events.table.reason_code')}</TableCell>
                  <TableCell>{t('events.table.user')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(item, t('events.no_timestamp'))}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/events/${item.id}`)}
                        sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ display: 'block' }}>
                          {item.event}
                        </Typography>
                      </Button>
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`events.outcome_${item.outcome}`, { defaultValue: item.outcome })}
                        color={getOutcomeColor(item.outcome)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.reasonCode ?? '-'}</TableCell>
                    <TableCell>{item.userId ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </Box>
        )}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('events.pagination_summary', {
              from: showingFrom,
              to: showingTo,
              total: totalCount,
            })}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage <= 1}
              onClick={() => changePage(currentPage - 1)}
            >
              ‹
            </Button>
            <Typography variant="body2">
              {currentPage} / {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage >= totalPages}
              onClick={() => changePage(currentPage + 1)}
            >
              ›
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Layout>
  );
};

export default EventsScreen;
